/**
 * AdminBusinessManagementTab — Super Admin's global Business Management
 * console. Server-side search across all TBC businesses (no hardcoded 500
 * cap), filter chips, pagination, and a right-column "Currently editing"
 * panel with quick actions that deep-link into the owner Dashboard via
 * ?admin_business_id=biz_xxx impersonation.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Loader2, Store, MapPin, Mail, User as UserIcon, Crown, BadgeCheck,
  ChevronLeft, ChevronRight, Package, Eye, Settings, QrCode, Sparkles,
  Filter, RotateCcw, ArrowRight, Info, ShieldAlert, ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

const STATUS_OPTIONS = [
  ["all", "All statuses"],
  ["active", "Active"],
  ["draft", "Draft"],
  ["suspended", "Suspended"],
  ["archived", "Archived"],
];
const OWNERSHIP_OPTIONS = [
  ["all", "All ownership"],
  ["claimed", "Claimed"],
  ["unclaimed", "Unclaimed"],
];
const VERIFICATION_OPTIONS = [
  ["all", "All"],
  ["verified", "Verified"],
  ["unverified", "Unverified"],
];
const MEMBERSHIP_OPTIONS = [
  ["all", "All memberships"],
  ["founding", "Founding"],
  ["paid", "Paid plan"],
  ["free", "Free"],
];
const SORT_OPTIONS = [
  ["name_asc", "Name A → Z"],
  ["name_desc", "Name Z → A"],
  ["newest", "Newest added"],
  ["oldest", "Oldest added"],
  ["updated", "Recently updated"],
  ["founding", "Founding position"],
  ["verified_first", "Verified first"],
];
const PAGE_SIZE = 50;

export default function AdminBusinessManagementTab() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [ownership, setOwnership] = useState("all");
  const [verification, setVerification] = useState("all");
  const [membership, setMembership] = useState("all");
  const [sort, setSort] = useState("name_asc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState({ businesses: [], total: 0, page: 1, page_count: 1, kpi: null });
  const [selected, setSelected] = useState(null);
  const [advanced, setAdvanced] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        q: q || undefined,
        status: status !== "all" ? status : undefined,
        ownership: ownership !== "all" ? ownership : undefined,
        verification: verification !== "all" ? verification : undefined,
        membership: membership !== "all" ? membership : undefined,
        sort,
        page,
        limit: PAGE_SIZE,
      };
      const r = await axios.get(`${API}/admin/businesses/search`, {
        params,
        withCredentials: true,
      });
      setResults(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  }, [q, status, ownership, verification, membership, sort, page]);

  useEffect(() => {
    search();
  }, [search]);

  // Reset to page 1 whenever the query or filters change (but not on paging itself).
  useEffect(() => {
    setPage(1);
  }, [q, status, ownership, verification, membership, sort]);

  const clearFilters = () => {
    setQ("");
    setStatus("all");
    setOwnership("all");
    setVerification("all");
    setMembership("all");
    setSort("name_asc");
    setPage(1);
  };

  const kpi = results.kpi;

  return (
    <div className="space-y-5" data-testid="admin-business-management-tab">
      {/* Page header */}
      <div>
        <h2 className="font-heading text-3xl text-tbc-navy">Business Management</h2>
        <p className="text-sm text-tbc-navy/60 mt-1">Search and manage all businesses in TheBlkCommunity.</p>
      </div>

      {/* KPI strip */}
      {kpi && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiTile icon={Store}      label="Total Businesses" value={kpi.total}     sub="All businesses in TBC" testid="kpi-total" />
          <KpiTile icon={Sparkles}   label="Active"           value={kpi.active}    sub={`${percent(kpi.active, kpi.total)} of total`} testid="kpi-active" tone="emerald" />
          <KpiTile icon={BadgeCheck} label="Verified"         value={kpi.verified}  sub={`${percent(kpi.verified, kpi.total)} of total`} testid="kpi-verified" tone="navy" />
          <KpiTile icon={Crown}      label="Founding"         value={kpi.founding}  sub={`${percent(kpi.founding, kpi.total)} of total`} testid="kpi-founding" tone="gold" />
        </div>
      )}

      {/* Search + filters */}
      <div className="bg-white rounded-2xl border border-tbc-navy/10 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-tbc-navy/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search businesses by name, owner, email, city, category or ID…"
              className="w-full pl-10 pr-3 py-2.5 rounded-full border border-tbc-navy/15 text-sm focus:outline-none focus:border-tbc-navy"
              data-testid="admin-biz-search-input"
            />
          </div>
          <Button
            variant="outline"
            className="rounded-full border-tbc-navy/15 text-tbc-navy hover:bg-tbc-navy hover:text-white"
            onClick={() => setAdvanced((v) => !v)}
            data-testid="admin-biz-advanced-toggle"
          >
            <Filter className="w-4 h-4 mr-1.5" />
            {advanced ? "Hide filters" : "Advanced filters"}
          </Button>
          <Button
            variant="ghost"
            className="text-tbc-navy/50 hover:text-tbc-navy"
            onClick={clearFilters}
            data-testid="admin-biz-clear-filters"
          >
            Clear
          </Button>
        </div>
        {advanced && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 pt-2 border-t border-tbc-navy/10">
            <Select label="Status"       value={status}       onChange={setStatus}       options={STATUS_OPTIONS}       testid="filter-status" />
            <Select label="Ownership"    value={ownership}    onChange={setOwnership}    options={OWNERSHIP_OPTIONS}    testid="filter-ownership" />
            <Select label="Verification" value={verification} onChange={setVerification} options={VERIFICATION_OPTIONS} testid="filter-verification" />
            <Select label="Membership"   value={membership}   onChange={setMembership}   options={MEMBERSHIP_OPTIONS}   testid="filter-membership" />
            <Select label="Sort"         value={sort}         onChange={setSort}         options={SORT_OPTIONS}         testid="filter-sort" />
          </div>
        )}
      </div>

      {/* Split layout: results | selected */}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-4">
        {/* LEFT: results */}
        <div className="bg-white rounded-2xl border border-tbc-navy/10 overflow-hidden flex flex-col min-h-[400px]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-tbc-navy/10">
            <h3 className="font-heading text-tbc-navy text-lg">
              Businesses
              <span className="ml-2 text-sm text-tbc-navy/50 font-normal">({results.total.toLocaleString()})</span>
            </h3>
            <span className="text-xs text-tbc-navy/50">
              Page {results.page} of {results.page_count}
            </span>
          </div>
          <div className="flex-1 divide-y divide-tbc-navy/10 overflow-y-auto max-h-[70vh]">
            {loading ? (
              <div className="p-12 flex items-center justify-center text-tbc-navy/50">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading…
              </div>
            ) : results.businesses.length === 0 ? (
              <div className="p-12 text-center text-tbc-navy/50 text-sm">No businesses match those filters.</div>
            ) : (
              results.businesses.map((b) => (
                <BusinessRow
                  key={b.business_id}
                  biz={b}
                  active={selected?.business_id === b.business_id}
                  onClick={() => setSelected(b)}
                />
              ))
            )}
          </div>
          {results.page_count > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-tbc-navy/10 bg-tbc-cream/30">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={results.page <= 1}
                data-testid="admin-biz-prev-page"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <span className="text-xs text-tbc-navy/60">
                Showing {(results.page - 1) * PAGE_SIZE + 1}–{Math.min(results.page * PAGE_SIZE, results.total)} of{" "}
                {results.total.toLocaleString()}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPage((p) => Math.min(results.page_count, p + 1))}
                disabled={results.page >= results.page_count}
                data-testid="admin-biz-next-page"
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* RIGHT: currently editing */}
        <div className="bg-white rounded-2xl border border-tbc-navy/10 overflow-hidden min-h-[400px]">
          {selected ? (
            <SelectedBusinessPanel biz={selected} onBack={() => setSelected(null)} onSaved={search} />
          ) : (
            <EmptySelection />
          )}
        </div>
      </div>
    </div>
  );
}

function percent(part, whole) {
  if (!whole) return "0%";
  return `${Math.round((part / whole) * 1000) / 10}%`;
}

function KpiTile({ icon: Icon, label, value, sub, tone, testid }) {
  const toneCls =
    tone === "gold" ? "text-tbc-gold" :
    tone === "emerald" ? "text-emerald-600" :
    tone === "navy" ? "text-tbc-navy" :
    "text-tbc-navy";
  return (
    <div className="rounded-2xl border border-tbc-navy/10 bg-white p-4 flex items-start gap-3" data-testid={testid}>
      <div className="flex-none w-10 h-10 rounded-full bg-tbc-cream flex items-center justify-center">
        <Icon className={`w-4 h-4 ${toneCls}`} />
      </div>
      <div className="min-w-0">
        <p className={`font-heading text-2xl leading-none ${toneCls}`}>{Number(value).toLocaleString()}</p>
        <p className="mt-1 text-[10px] uppercase tracking-widest text-tbc-navy/50">{label}</p>
        {sub && <p className="text-[11px] text-tbc-navy/40 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="text-[10px] uppercase tracking-widest text-tbc-navy/50 flex flex-col gap-1">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm border border-tbc-navy/15 rounded-lg px-2 py-1.5 text-tbc-navy bg-white focus:outline-none focus:border-tbc-navy"
        data-testid={testid}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </label>
  );
}

function BusinessRow({ biz, active, onClick }) {
  const badges = [];
  if (biz.is_verified) badges.push({ label: "Verified", tone: "emerald" });
  if (biz.is_claimed) badges.push({ label: "Claimed", tone: "navy" });
  else badges.push({ label: "Unclaimed", tone: "slate" });
  if (biz.is_founding_member && biz.founding_member_number) badges.push({ label: `Founding #${biz.founding_member_number}`, tone: "gold" });
  if (biz.subscription_tier && biz.subscription_tier !== "free") badges.push({ label: "Paid", tone: "emerald" });

  // Stripe payout status — derived from the mirrored flags on the biz doc
  // so we don't burn an extra API call per row. Same logic as the backend
  // `derive_stripe_state()` helper.
  const stripeState = (() => {
    if (!biz.stripe_account_id) return null;  // hide chip when never started
    if (biz.stripe_charges_enabled) return { label: "Payouts Active", tone: "emerald" };
    const reqs = biz.stripe_requirements || {};
    if (reqs.disabled_reason) return { label: "Payouts Restricted", tone: "red" };
    const due = reqs.currently_due || [];
    if (biz.stripe_details_submitted && due.length === 0) return { label: "Under Review", tone: "blue" };
    if (biz.stripe_details_submitted && due.length > 0) return { label: "Verify Required", tone: "amber" };
    return { label: "Setup Incomplete", tone: "amber" };
  })();
  if (stripeState) badges.push(stripeState);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 hover:bg-tbc-cream/40 transition-colors ${active ? "bg-tbc-cream/60 border-l-4 border-tbc-gold pl-3" : ""}`}
      data-testid={`admin-biz-row-${biz.business_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-tbc-navy truncate">{biz.name || "(unnamed)"}</p>
          <p className="text-xs text-tbc-navy/60 mt-0.5 flex items-center gap-1 truncate">
            {biz.category || "—"}
            <span className="text-tbc-navy/25">·</span>
            <MapPin className="w-3 h-3" />
            {[biz.city, biz.state].filter(Boolean).join(", ") || "No location"}
          </p>
          {biz.owner?.email && (
            <p className="text-[11px] text-tbc-navy/50 mt-1 flex items-center gap-1 truncate">
              <Mail className="w-3 h-3" />
              {biz.owner.email}
            </p>
          )}
        </div>
        <div className="text-right flex-none">
          <p className="font-mono text-[10px] text-tbc-navy/50">{biz.business_id}</p>
          <div className="flex flex-wrap justify-end gap-1 mt-1">
            {badges.map((b) => (
              <Badge key={`${b.tone}:${b.label}`} tone={b.tone}>{b.label}</Badge>
            ))}
          </div>
        </div>
      </div>
    </button>
  );
}

function Badge({ tone, children }) {
  const cls =
    tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    tone === "gold" ? "bg-tbc-gold/15 text-tbc-navy border-tbc-gold/40" :
    tone === "navy" ? "bg-tbc-navy/5 text-tbc-navy border-tbc-navy/15" :
    tone === "slate" ? "bg-slate-50 text-slate-600 border-slate-200" :
    tone === "red" ? "bg-red-50 text-red-700 border-red-200" :
    tone === "blue" ? "bg-blue-50 text-blue-700 border-blue-200" :
    tone === "amber" ? "bg-amber-50 text-amber-800 border-amber-200" :
    "bg-white text-tbc-navy/60 border-tbc-navy/10";
  return <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${cls}`}>{children}</span>;
}

function EmptySelection() {
  return (
    <div className="h-full flex items-center justify-center text-center p-12">
      <div>
        <div className="w-14 h-14 mx-auto rounded-full bg-tbc-cream flex items-center justify-center">
          <Store className="w-6 h-6 text-tbc-navy/40" />
        </div>
        <p className="mt-4 font-heading text-lg text-tbc-navy">Select a business to begin editing</p>
        <p className="mt-1 text-sm text-tbc-navy/50 max-w-xs mx-auto">Tap any row on the left to switch into Super Admin edit mode for that business.</p>
      </div>
    </div>
  );
}

function SelectedBusinessPanel({ biz, onBack, onSaved }) {
  const ownerEmail = biz.owner?.email;
  // The Dashboard reads ?admin_business_id=... and switches its context.
  const dashLink = (extra = "") => {
    const base = `/dashboard?admin_business_id=${encodeURIComponent(biz.business_id)}`;
    return extra ? `${base}&${extra}` : base;
  };
  return (
    <div className="p-5" data-testid="admin-biz-selected-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-tbc-navy/50 font-semibold">Currently Editing</p>
          <h3 className="font-heading text-2xl text-tbc-navy mt-1 truncate">
            {biz.name}
            {biz.is_verified && <BadgeCheck className="w-4 h-4 text-tbc-gold inline ml-1" />}
          </h3>
          <p className="text-xs text-tbc-navy/60 mt-0.5">
            {biz.category || "—"}
            {biz.city ? ` · ${biz.city}${biz.state ? `, ${biz.state}` : ""}` : ""}
            {ownerEmail ? ` · Owner: ${ownerEmail}` : ""}
          </p>
          <p className="text-[11px] font-mono text-tbc-navy/50 mt-1">Business ID: {biz.business_id}</p>
        </div>
        <div className="flex flex-col items-end gap-2 flex-none">
          <Link
            to={`/business/${biz.business_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-tbc-navy/70 hover:text-tbc-navy underline underline-offset-2"
            data-testid="admin-biz-view-storefront"
          >
            <Eye className="w-3.5 h-3.5" />
            View storefront
          </Link>
          <Button
            size="sm"
            variant="ghost"
            className="text-tbc-navy/60 hover:text-tbc-navy"
            onClick={onBack}
            data-testid="admin-biz-back-to-search"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to search
          </Button>
        </div>
      </div>

      {/* Super Admin mode banner */}
      <div className="mt-4 rounded-xl border border-tbc-gold/40 bg-tbc-gold/10 p-3 flex items-start gap-2" data-testid="admin-biz-super-admin-banner">
        <ShieldAlert className="w-4 h-4 text-tbc-gold flex-none mt-0.5" />
        <div className="text-xs text-tbc-navy/85 leading-relaxed">
          <strong className="text-tbc-navy">SUPER ADMIN MODE</strong> — you are editing this business as an administrator. Every save is written to the admin audit log.
        </div>
      </div>

      {/* Quick actions — reuse existing owner flows via ?admin_business_id */}
      <div className="mt-5 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-tbc-navy/50 font-semibold">Quick actions</p>
        <div className="grid grid-cols-2 gap-2">
          <QuickAction
            to={dashLink("tab=products&action=new")}
            icon={Package}
            label="Add New Product"
            primary
            testid="admin-biz-action-add-product"
          />
          <QuickAction to={`/business/${biz.business_id}`} icon={Eye} label="Preview Storefront" newTab testid="admin-biz-action-preview" />
          <QuickAction to={dashLink("tab=settings")} icon={Settings} label="Update Business Info" testid="admin-biz-action-info" />
          <QuickAction to={dashLink("tab=orders")}   icon={ClipboardList} label="Manage Orders" testid="admin-biz-action-orders" />
          <QuickAction to={dashLink("tab=qr")}       icon={QrCode} label="Business QR Cards" testid="admin-biz-action-qr" />
          <QuickAction to={dashLink("tab=products")} icon={Store} label="Manage Storefront" testid="admin-biz-action-store" />
        </div>
      </div>

      {/* At-a-glance summary */}
      <div className="mt-5 rounded-xl bg-tbc-cream/40 border border-tbc-navy/10 p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
        <SummaryLine label="Status" value={biz.subscription_status || (biz.is_verified ? "Verified" : "Draft")} />
        <SummaryLine label="Tier" value={biz.subscription_tier || "free"} />
        <SummaryLine label="Founding" value={biz.founding_member_number ? `#${biz.founding_member_number}` : "—"} />
        <SummaryLine label="Website" value={(biz.website || biz.website_url) ? shorten(biz.website || biz.website_url) : "—"} />
        <SummaryLine label="Phone" value={biz.phone || "—"} />
        <SummaryLine label="Email" value={biz.email || "—"} />
      </div>

      <p className="mt-4 text-[11px] text-tbc-navy/50 leading-relaxed">
        Quick actions open the standard business dashboard scoped to <span className="font-mono">{biz.business_id}</span>. When you save any change, the audit log records the action against your admin account.
      </p>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, primary, newTab, testid }) {
  const cls = primary
    ? "bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 border-transparent"
    : "bg-white text-tbc-navy hover:bg-tbc-cream/60 border-tbc-navy/15";
  return (
    <Link
      to={to}
      target={newTab ? "_blank" : undefined}
      rel={newTab ? "noopener noreferrer" : undefined}
      className={`rounded-xl border px-4 py-3 flex items-center gap-2 transition-colors ${cls}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-tbc-navy/40">{label}</p>
      <p className="text-tbc-navy truncate">{value}</p>
    </div>
  );
}

function shorten(url) {
  try {
    return new URL(url).host;
  } catch {
    return url.length > 24 ? `${url.slice(0, 24)}…` : url;
  }
}
